import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import { useSearchPerson, useLoadPeople } from "src/hooks/useSearchPeople";
import TeamDropdown from "src/components/TeamDropdown";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import { isTeamOwnerEnabled } from "src/util/orgDesignDirectFlags";
import { fullDisplayName } from "src/util/personName";

import PersonFormatOptionLabel from "./PersonSelect/PersonFormatOptionLabel";

const TagAttributePersonSelect = ({
  id = "tag-attribute-person-select",
  label,
  onSelectPerson,
  value,
  person,
  disabled,
  invalid,
  mandatory,
  focus,
}) => {
  const [selectedOption, setSelectedOption] = useState<{
    label?: string;
    value?: string;
    person?: unknown;
  } | null>(null);
  // Team owners are on via Org Design or the stored team owner flag: show the
  // supply (owner) chain, else the HRIS chain.
  const useSupplyChain = isTeamOwnerEnabled(useWorkspaceFeatureFlags());
  const reportingLineOptions = {
    withReportingLine: true,
    includeSupplyReportingLine: useSupplyChain,
  };
  const { loadPersonOptions, isSearching } = useSearchPerson({
    fetchPolicy: "cache-first",
    ...reportingLineOptions,
  });
  const { loadPeople, isLoading } = useLoadPeople(reportingLineOptions);

  const formatOptionLabel = useMemo(
    () => (option) =>
      PersonFormatOptionLabel({
        label: option.label,
        person: option.person,
        useSupplyChain,
      }),
    [useSupplyChain]
  );

  useEffect(() => {
    // A person passed in without a manager chain can't show the breadcrumb, so
    // reload it by id when we can.
    if (person && ("managedByHierarchy" in person || !value)) {
      setSelectedOption({
        label: fullDisplayName(person),
        value,
        person,
      });
    } else if (value) {
      loadPeople([value]).then((loadedPeople) => {
        if (isEmpty(loadedPeople)) {
          return;
        }
        setSelectedOption({
          label: fullDisplayName(loadedPeople[0]),
          value,
          person: loadedPeople[0],
        });
      });
    } else {
      setSelectedOption(null);
    }
  }, [loadPeople, person, value]);

  return (
    <TeamDropdown
      key={id}
      id={id}
      label={label}
      placeholder="Search person by name or id"
      isSearchable
      isLoading={isSearching || isLoading}
      isDisabled={disabled}
      options={true}
      loadOptions={loadPersonOptions}
      onSelectTeam={onSelectPerson}
      value={selectedOption}
      isClearable={!disabled}
      formatOptionLabel={formatOptionLabel}
      invalid={invalid}
      mandatory={mandatory}
      focus={focus}
    />
  );
};

TagAttributePersonSelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  person: PropTypes.object,
  value: PropTypes.string,
  onSelectPerson: PropTypes.func,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  mandatory: PropTypes.bool,
  focus: PropTypes.bool,
};
export default TagAttributePersonSelect;
